import type { ColorScheme, MiniAppBridge, ViewportState } from './types';
import {
  createEmitter,
  createLocalStorage,
  emptyInsets,
  noopUnsubscribe,
  readLaunchParamsFromUrl,
  saveFile,
} from './shared';

/**
 * Веб-адаптер: приложение открыто в обычном браузере (локальная разработка,
 * дизайн-ревью, QA). Системные элементы мессенджера эмулируются средствами
 * самого приложения — за это отвечает UI-слой, а не адаптер.
 */
export function createWebBridge(): MiniAppBridge {
  const storage = createLocalStorage('bd-web');
  const themeEmitter = createEmitter<ColorScheme>();
  const viewportEmitter = createEmitter<ViewportState>();

  const media = window.matchMedia('(prefers-color-scheme: dark)');
  media.addEventListener('change', (event) => themeEmitter.emit(event.matches ? 'dark' : 'light'));

  const readViewport = (): ViewportState => ({
    height: window.visualViewport?.height ?? window.innerHeight,
    stableHeight: window.innerHeight,
    isExpanded: true,
  });
  window.addEventListener('resize', () => viewportEmitter.emit(readViewport()));
  window.visualViewport?.addEventListener('resize', () => viewportEmitter.emit(readViewport()));

  const backEmitter = createEmitter<void>();

  return {
    id: 'web',
    name: 'Браузер',
    isEmbedded: false,

    ready: () => undefined,
    expand: () => undefined,
    close: () => window.history.back(),

    /**
     * Данные запуска берутся из адреса.
     *
     * Сюда попадают и запуски из мессенджера, который не выставил своего
     * объекта на странице: параметры при этом лежат в адресе, и терять их
     * из-за неопознанной платформы нельзя — без них вход невозможен.
     */
    getInitData: () => readLaunchParamsFromUrl(),
    // Пользователя без мессенджера нет: выдуманный показывал бы чужое имя
    getUser: () => null,

    getColorScheme: () => (media.matches ? 'dark' : 'light'),
    onColorSchemeChange: (handler) => themeEmitter.subscribe(handler),

    getViewport: readViewport,
    onViewportChange: (handler) => viewportEmitter.subscribe(handler),
    getSafeAreaInsets: () => emptyInsets,

    setBackButtonVisible: () => undefined,
    onBackButton: (handler) => backEmitter.subscribe(handler),

    setMainButton: () => undefined,
    onMainButton: () => noopUnsubscribe,

    haptic: {
      impact: () => navigator.vibrate?.(8),
      notification: () => navigator.vibrate?.([10, 40, 10]),
      selection: () => navigator.vibrate?.(4),
    },

    storage,

    openLink: (url) => window.open(url, '_blank', 'noopener'),
    openInternalLink: (url) => window.open(url, '_blank', 'noopener'),
    share({ url, text }) {
      const payload = { url, text, title: 'Бренд врача' };
      if (navigator.share) void navigator.share(payload).catch(() => undefined);
      else void navigator.clipboard?.writeText([text, url].filter(Boolean).join(' '));
    },

    requestWriteAccess: async () => true,

    async downloadFile({ url, fileName, blob }) {
      return saveFile({ url, fileName, blob });
    },

    showConfirm: async (message) => window.confirm(message),
    showAlert: async (message) => {
      window.alert(message);
    },
  };
}
